import { cn } from '@/lib/cn'
import { SITE_NAME, SITE_TAGLINE } from '@/lib/site'

/**
 * The brand lockup: the supplied mark as an image, with the wordmark set in
 * live text.
 *
 * Keeping the words as text rather than baking them into the bitmap means they
 * stay crisp at any zoom, respond to the layout, and are selectable and
 * translatable. The mark is decorative here because the adjacent text already
 * names the product.
 */
export function Logo({
  tone = 'light',
  size = 'md',
}: {
  tone?: 'light' | 'dark'
  size?: 'sm' | 'md'
}) {
  // The mark carries fine circuit detail, so it needs a little more height
  // than a flat glyph would to stay legible.
  const markSize = size === 'sm' ? 'h-8' : 'h-9 sm:h-11'

  return (
    <span className="flex min-w-0 items-center gap-2.5 sm:gap-3">
      <img
        src="/brand/bis-mark-128.png"
        srcSet="/brand/bis-mark-128.png 1x, /brand/bis-mark-256.png 2x"
        alt=""
        aria-hidden="true"
        width={128}
        height={155}
        loading="eager"
        decoding="async"
        className={cn(
          'w-auto flex-none object-contain transition-transform duration-500 ease-brand group-hover:scale-105',
          markSize,
        )}
      />
      {/* The product name leads the lockup; the company sits under it as the
          quieter attribution line. */}
      <span className="min-w-0 leading-none">
        <strong
          className={cn(
            'block truncate text-[12.5px] font-extrabold leading-tight tracking-[-0.02em] sm:text-[13.5px]',
            tone === 'dark' ? 'text-white' : 'text-ink',
          )}
        >
          {SITE_TAGLINE}
        </strong>
        <span
          className={cn(
            'mt-1 block truncate text-[10.5px] not-italic leading-tight tracking-[0.01em] sm:text-[11px]',
            tone === 'dark' ? 'text-white/60' : 'text-muted-soft',
          )}
        >
          {SITE_NAME}
        </span>
      </span>
    </span>
  )
}
